import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ArrowRight, LucideIconData } from 'lucide-angular';
import { Product, AddToCartEvent } from '../../../../shared/models/product.model';
import { Category } from '../../../../shared/models/category.model';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { ProductCarouselComponent } from '../product-carousel/product-carousel.component';

// Shape de cada tab del bloque. Vive aquí (en el dueño del UI) en vez de
// importarse desde `home.service.ts`, que se eliminará en cleanup post-API.
export interface CategoryProductsTab {
  category: Category;
  products: Product[];
}

/**
 * Bloque genérico del home: título + link al catálogo + tabs por categoría +
 * carrusel de productos del tab activo. Es un componente "tonto": recibe los
 * tabs ya resueltos desde fuera y reemite los eventos del carrusel. La carga
 * de datos (incluido el manejo de loading/error) la hace el wrapper concreto
 * (`new-releases-block`, `perron-singles-block`, etc.) — así cada wrapper
 * decide su fuente en `HomeService` sin acoplar este componente a un endpoint.
 */
@Component({
  selector: 'app-tabbed-carousel-block',
  imports: [ProductCarouselComponent, IconComponent, RouterLink],
  templateUrl: './tabbed-carousel-block.component.html',
  styleUrl: './tabbed-carousel-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabbedCarouselBlockComponent {
  title = input.required<string>();
  ariaLabel = input<string>('Filtrar por categoría');
  viewAllLabel = input<string>('Ver catálogo');
  viewAllLink = input<string>('/tienda');
  tabs = input.required<CategoryProductsTab[]>();
  loading = input<boolean>(false);
  errored = input<boolean>(false);
  shouldRender = input<boolean>(true);
  errorMessage = input<string>(
    'No pudimos cargar los productos. Intenta recargar la página.',
  );
  // Permite alternar el fondo entre las dos superficies neutras del design
  // system (blanco / gris claro). El consumidor decide para mantener la regla
  // de "fondos alternados" cuando dos bloques compartidos van pegados.
  surface = input<'primary' | 'secondary'>('secondary');

  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  reserveClick = output<Product>();

  // Id de la categoría activa. `null` mientras no haya tabs cargadas;
  // el effect de abajo lo fija al primer tab cuando llega la data.
  protected readonly activeCategoryId = signal<number | null>(null);

  protected readonly activeProducts = computed<Product[]>(() => {
    const id = this.activeCategoryId();
    if (id === null) return [];
    const tab = this.tabs().find((t) => t.category.id === id);
    return tab?.products ?? [];
  });

  protected readonly arrowRightIcon: LucideIconData = ArrowRight;

  constructor() {
    // Cuando terminan de cargarse los tabs, fijamos el primero como activo.
    // Usamos effect + guard en lugar de un `initialValue` para no asumir orden
    // de categorías: el backend decide cuál es la primera.
    effect(() => {
      const tabs = this.tabs();
      if (tabs.length > 0 && this.activeCategoryId() === null) {
        this.activeCategoryId.set(tabs[0].category.id);
      }
    });
  }

  protected selectCategory(id: number): void {
    this.activeCategoryId.set(id);
  }

  protected isActive(id: number): boolean {
    return this.activeCategoryId() === id;
  }
}
